/**
 * Review a pull request now: a number or a URL, and the repository it is in.
 * One `POST /api/code-review/desks/:deskId/review-requests` to the Team that
 * reviews that repository, which reads it at that Team's own level and posts
 * like any other review. The request carries no level of its own, so the
 * sheet offers none.
 */
import { useMemo, useState } from "react";
import type { RepoClaimRow } from "@yagni/shared";
import { Chip, RecordHead, SheetFoot } from "../queue/index.js";
import type { ReviewsApi } from "./reviewsApi.js";

export interface NewReviewSheetProps {
  api: ReviewsApi;
  /** Who reviews which repository; only repositories a readable Team reviews are offered. */
  claims: readonly RepoClaimRow[];
  onQueued: (prStateId: string, deskId: string) => void;
  onCancel: () => void;
}

/** `owner/name` out of a pull request URL, lowercased, or null. */
export function repoFromPrUrl(input: string): string | null {
  const match = /github\.com\/([^/\s]+)\/([^/\s]+)\/pull\/\d+/i.exec(input);
  return match ? `${match[1]}/${match[2]}`.toLowerCase() : null;
}

export function NewReviewSheet({ api, claims, onQueued, onCancel }: NewReviewSheetProps) {
  const repos = useMemo(
    () => claims.filter((claim): claim is RepoClaimRow & { deskId: string } => claim.deskId != null),
    [claims],
  );
  const [input, setInput] = useState("");
  const [repo, setRepo] = useState(repos[0]?.repo ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fromUrl = repoFromPrUrl(input);
  const target = repos.find((claim) => claim.repo === (fromUrl ?? repo)) ?? null;
  const ready = input.trim() !== "" && target != null;

  const submit = async () => {
    if (!target) return;
    setBusy(true);
    setError(null);
    const result = await api.requestReview(target.deskId, { input: input.trim(), repo: target.repo });
    setBusy(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    onQueued(result.value.prStateId, target.deskId);
  };

  return (
    <>
      <RecordHead
        recordKey="REVIEW NOW"
        title="Review a pull request"
        noExpand
        onClose={onCancel}
        meta={
          <>
            <Chip>Proctor</Chip>
            <span className="fact" style={{ fontSize: 11.5 }}>
              one read, posted like any other
            </span>
          </>
        }
      />
      <div className="sbody">
        <div className="field top">
          <label htmlFor="new-review-pr">Pull request</label>
          <div className="in mono">
            <input
              id="new-review-pr"
              placeholder="number or URL, e.g. 4412"
              value={input}
              autoFocus
              onChange={(event) => setInput(event.target.value)}
            />
          </div>
          <div className="help">A pull request on any repository a Team here reviews.</div>
        </div>
        <div className="field">
          <label htmlFor="new-review-repo">In</label>
          {fromUrl ? (
            <span className="fact" data-testid="new-review-repo-from-url">
              {fromUrl}
              {target ? ` · ${target.deskName ?? "a Team"}` : " · no Team you can see reviews it"}
            </span>
          ) : (
            <select id="new-review-repo" value={repo} onChange={(event) => setRepo(event.target.value)}>
              {repos.map((claim) => (
                <option key={claim.repo} value={claim.repo}>
                  {claim.repo}
                </option>
              ))}
            </select>
          )}
        </div>
        {repos.length === 0 && <p className="body-p">No Team you can see reviews a repository yet.</p>}
        {error && (
          <p className="body-p" role="alert" data-testid="new-review-error">
            {error}
          </p>
        )}
      </div>
      <SheetFoot
        why={
          <>
            Proctor reads it now and posts one review. <b>Its level and whether it advises or approves follow the repository's Team.</b>
          </>
        }
        secondary={{ label: "Cancel", onClick: onCancel }}
        primary={{ label: "Review it", onClick: () => void submit(), disabled: !ready || busy }}
      />
    </>
  );
}
